import { useAtomValue, useSetAtom } from 'jotai';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { Services } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	Drawer,
	Group,
	Stack,
	Tabs,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { useSearchConversations } from '@common/api/conversations/useSearchConversations';
import { ConversationView } from '@common/components/conversations/ConversationView';
import {
	ChevronDown12,
	ChevronDown24,
	ChevronLeft24,
	ChevronUp24,
	SquareUp24,
} from '@common/components/icons';
import { NavStack } from '@common/components/NavStack';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { PLACEHOLDER } from '@common/lib/strings';

import { useExportLeads } from '../api/useExportLeads';
import { useSearchLeads } from '../api/useSearchLeads';
import {
	conversationNavAtom,
	conversationNavHandlerAtom,
	navigateBackFromConversationAtom,
	resetConversationNavAtom,
} from '../atoms/conversationAtoms';
import { leadDetailsDrawerAtom, LeadDetailsTab } from '../atoms/leadDetailsDrawerAtoms';
import { leadsParamsAtom } from '../atoms/leadsAtoms';
import { useLeadNavigation } from '../hooks/useLeadNavigation';

import { ConversationsTabContent } from './lead-details/ConversationsTabContent';
import { OverviewTabContent } from './lead-details/OverviewTabContent';

import '@common/components/_drawerShared.scss';

// CXP-2321: Pre-qual badge hidden until prequal is functional
// import { PrequalBadge } from './PrequalBadge';

export const LeadDetailsDrawer = () => {
	// Get URL params
	const { leadId, detailsTab } = useParams<{
		leadId?: string;
		detailsTab?: string;
	}>();
	const {
		closeLeadDetails,
		switchLeadDetailsTab,
		navigateToPreviousLead,
		navigateToNextLead,
		getNavigationState,
	} = useLeadNavigation();

	// Drawer is open if leadId and detailsTab are present in URL
	const isDrawerOpen = Boolean(leadId && detailsTab);
	const activeTab = (detailsTab as LeadDetailsTab) || LeadDetailsTab.OVERVIEW;

	// Get leads data to find the selected lead
	const params = useAtomValue(leadsParamsAtom);
	const { data } = useSearchLeads(params);
	const selectedRow = data?.data.find((lead) => lead.id === leadId) ?? null;
	const totalCount = data?.count ?? 0;

	// Get navigation state for prev/next buttons
	const navigationState = leadId
		? getNavigationState(leadId, totalCount)
		: { canGoPrevious: false, canGoNext: false };

	// Sync selectedRow to atom for tab content components
	const setDrawerState = useSetAtom(leadDetailsDrawerAtom);
	useEffect(() => {
		setDrawerState({ selectedRow });
	}, [selectedRow, setDrawerState]);

	// Conversation NavStack state
	const {
		viewId: conversationViewId,
		selectedConversationId,
		currentTitle: conversationTitle,
	} = useAtomValue(conversationNavAtom);
	const conversationNavHandler = useAtomValue(conversationNavHandlerAtom);
	const navigateBack = useSetAtom(navigateBackFromConversationAtom);
	const resetConversationNav = useSetAtom(resetConversationNavAtom);
	const isViewingConversation =
		activeTab === LeadDetailsTab.CONVERSATIONS && conversationViewId === 'conversation-thread';

	// Reset conversation nav when lead changes (leads can switch via prev/next while drawer stays open)
	useEffect(() => {
		resetConversationNav();
	}, [selectedRow?.id, resetConversationNav]);

	// BDC feature gate — config is a plain object from the API, not a class instance
	const { data: servicesProvider } = useCurrentServicesProvider();
	const isProduction = import.meta.env.VITE_CURR_ENV === 'production';
	const isBdcEnabled =
		!isProduction && servicesProvider?.config?.[Services.BDC]?.supported === true;

	// Conversation count for tab badge
	const { conversations, isLoading: conversationsPending } = useSearchConversations({
		filters: { leadId: isBdcEnabled ? leadId : undefined },
	});
	const conversationCount = conversationsPending ? PLACEHOLDER : String(conversations.length);

	// Export leads mutation
	const exportLeadsMutation = useExportLeads();

	// Close drawer handler
	const closeDrawer = () => {
		resetConversationNav();
		closeLeadDetails();
	};

	// Set active tab handler
	const setActiveTab = (tab: LeadDetailsTab) => {
		if (!leadId) return;
		switchLeadDetailsTab(leadId, tab);
	};

	const handleExportAsCsv = async () => {
		if (!selectedRow) return;

		try {
			await exportLeadsMutation.mutateAsync({
				leadIds: [selectedRow.id],
			});

			toast({
				message: 'Lead exported',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export lead. Please try again.',
				variant: 'error',
			});
		}
	};

	const handlePreviousLead = () => {
		if (leadId && navigationState.canGoPrevious) {
			navigateToPreviousLead(leadId, activeTab);
		}
	};

	const handleNextLead = () => {
		if (leadId && navigationState.canGoNext) {
			navigateToNextLead(leadId, activeTab);
		}
	};

	// Tab items configuration — Conversations tab only shown when BDC is enabled
	const tabItems: TabItem[] = [
		{ id: LeadDetailsTab.OVERVIEW, content: 'Overview' },
		...(isBdcEnabled
			? [
					{
						id: LeadDetailsTab.CONVERSATIONS,
						content: (
							<Group itemsSpacing='8' itemsAlignY='center' noWrap>
								<Group.Item>
									<span>Conversations</span>
								</Group.Item>
								<Group.Item>
									<Text size='footnote' color='secondary'>
										{conversationCount}
									</Text>
								</Group.Item>
							</Group>
						),
					},
				]
			: []),
	];

	const handleTabSelect = (item: TabItem) => {
		setActiveTab(item.id as LeadDetailsTab);
	};

	// Render the appropriate content based on the active tab
	const renderTabContent = () => {
		if (!selectedRow) {
			return null;
		}

		switch (activeTab) {
			case LeadDetailsTab.OVERVIEW:
				return <OverviewTabContent />;
			case LeadDetailsTab.CONVERSATIONS:
				return isBdcEnabled ? <ConversationsTabContent /> : <OverviewTabContent />;
		}
	};

	// Don't render if no lead is selected
	if (!selectedRow) {
		return null;
	}

	// Drawer title: conversation title with back button, or lead name
	const drawerTitle = isViewingConversation ? (
		<Group itemsSpacing='8' itemsAlignY='center'>
			<Button
				className='drawer-back-button--rev'
				variant='ghost'
				size='large'
				aria-label='Back'
				icon={<ChevronLeft24 />}
				onPress={navigateBack}
			/>
			<Text size='heading' weight='semibold'>
				{conversationTitle ?? 'Conversation'}
			</Text>
		</Group>
	) : (
		<Group itemsSpacing='8' itemsAlignY='center'>
			<Text size='heading' weight='semibold'>
				{(selectedRow.name || leadId) ?? ''}
			</Text>
			{/* CXP-2321: Pre-qual badge hidden until prequal is functional */}
			{/* <PrequalBadge status={selectedRow.preQualStatus} /> */}
		</Group>
	);

	return (
		<DialogTrigger
			isOpen={isDrawerOpen}
			onOpenChange={(open) => {
				if (!open) {
					closeDrawer();
				}
			}}>
			<Drawer
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Lead Details'
				className='drawer--custom'>
				<Drawer.Header>{drawerTitle}</Drawer.Header>
				<Drawer.Content padding={{ x: 24, top: 12, bottom: 64 }}>
					<Stack>
						<NavStack handler={conversationNavHandler}>
							{/* Root view — tabs + tab content */}
							<NavStack.View viewId='root'>
								<Stack itemsSpacing='16'>
									<Tabs
										items={tabItems}
										selectedKey={activeTab}
										onSelect={handleTabSelect}
										className='lead-details-tabs--custom'
									/>
									{renderTabContent()}
								</Stack>
							</NavStack.View>

							{/* Conversation thread view */}
							<NavStack.View viewId='conversation-thread'>
								<ConversationView
									leadId={selectedRow?.id}
									conversationId={selectedConversationId ?? undefined}
								/>
							</NavStack.View>
						</NavStack>
					</Stack>
				</Drawer.Content>
				{/* Footer with navigation buttons — hidden on conversation thread view */}
				{!isViewingConversation && (
					<Drawer.Footer>
						<motion.div
							key={String(conversationNavHandler.currentViewId)}
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.2, ease: 'easeOut' }}
							className='drawer-footer'>
							<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
								<Group.Item>
									<ActionMenuTrigger>
										<Button
											variant='secondary'
											size='large'
											icon={<ChevronDown12 />}
											iconSide='right'>
											More actions
										</Button>
										<ActionMenu>
											<ActionMenuItem
												label='Export lead as CSV'
												leadingAccessory={<SquareUp24 />}
												onAction={handleExportAsCsv}
											/>
										</ActionMenu>
									</ActionMenuTrigger>
								</Group.Item>
								<Group itemsSpacing='8' noWrap>
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronUp24 />}
										aria-label='Previous lead'
										onPress={handlePreviousLead}
										isDisabled={!navigationState.canGoPrevious}
									/>
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronDown24 />}
										aria-label='Next lead'
										onPress={handleNextLead}
										isDisabled={!navigationState.canGoNext}
									/>
								</Group>
							</Group>
						</motion.div>
					</Drawer.Footer>
				)}
			</Drawer>
		</DialogTrigger>
	);
};
